import { createStore, useStore } from 'zustand';
import {
  APP_COLOR_VALUES,
  BILLING_OVERLAP_STATUS_SSE_EVENT,
  BILLING_OVERLAP_STATUS_STREAM_PATH,
  type BillingOverlapPublicStatusDto,
  HeartbeatEventSource,
  jsonParseSafe,
  OVERLAP_PHASE_VALUES,
  type TAppColor,
} from '@skillarea/lib-shared';

import { MatchmakingService } from '~/services/matchmakingService';
import { PublicOverlapStatusService } from '~/services/publicOverlapStatusService';

const OVERLAP_POLL_INTERVAL_MS = 10000;
const SSE_OVERLAP_STATUS_API_URL = `/api/overlap-status/${BILLING_OVERLAP_STATUS_STREAM_PATH}`;

export const OVERLAP_DRAIN_MATCHMAKING_BLOCK_MESSAGE =
  'Server has been updated. Refresh the page before starting a new game.';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isAppColor(value: unknown): value is TAppColor {
  return (
    typeof value === 'string' &&
    (APP_COLOR_VALUES as readonly string[]).includes(value)
  );
}

function resolveClientColor(value: unknown): TAppColor | null {
  if (typeof value !== 'string') {
    return null;
  }

  const normalizedColor = value.trim().toLowerCase();

  return isAppColor(normalizedColor) ? normalizedColor : null;
}

const CLIENT_COLOR = resolveClientColor(import.meta.env.VITE_APP_COLOR);

function isOverlapPhase(
  value: unknown,
): value is BillingOverlapPublicStatusDto['phase'] {
  return (
    typeof value === 'string' &&
    (OVERLAP_PHASE_VALUES as readonly string[]).includes(value)
  );
}

function isBillingOverlapPublicStatusDto(
  value: unknown,
): value is BillingOverlapPublicStatusDto {
  if (!isRecord(value)) {
    return false;
  }

  if (!isOverlapPhase(value.phase)) {
    return false;
  }

  if (
    value.drainingColor !== undefined &&
    value.drainingColor !== null &&
    !isAppColor(value.drainingColor)
  ) {
    return false;
  }

  if (
    value.admittingColor !== undefined &&
    value.admittingColor !== null &&
    !isAppColor(value.admittingColor)
  ) {
    return false;
  }

  if (
    value.updatedAtMs !== undefined &&
    typeof value.updatedAtMs !== 'number'
  ) {
    return false;
  }

  return true;
}

function computeBannerVisible(
  overlapStatus: BillingOverlapPublicStatusDto | null,
  clientColor: TAppColor | null,
  isClientRefreshRequired: boolean,
): boolean {
  if (isClientRefreshRequired) {
    return true;
  }

  if (!overlapStatus) {
    return false;
  }

  if (overlapStatus.phase === 'idle') {
    return false;
  }

  if (!overlapStatus.drainingColor) {
    return false;
  }

  return clientColor === overlapStatus.drainingColor;
}

function shouldRequireClientRefresh(
  overlapStatus: BillingOverlapPublicStatusDto,
  clientColor: TAppColor | null,
): boolean {
  if (!clientColor) {
    return false;
  }

  if (overlapStatus.phase === 'idle') {
    return false;
  }

  return overlapStatus.drainingColor === clientColor;
}

type TOverlapState = {
  overlapStatus: BillingOverlapPublicStatusDto | null;
  clientColor: TAppColor | null;
  serverColor: TAppColor | null;
  isBannerVisible: boolean;
  isClientRefreshRequired: boolean;
  isLoggedIn: boolean;
  primedForVisibleAuthSession: boolean;
  syncOverlapStatus: (status: BillingOverlapPublicStatusDto) => void;
  syncServerColor: (color: TAppColor | null) => void;
  syncAuthState: (isLoggedIn: boolean) => void;
  refreshWithPrime: () => Promise<void>;
  subscribeOverlapStatus: () => () => void;
};

export const overlapStore = createStore<TOverlapState>((set, get) => ({
  overlapStatus: null,
  clientColor: CLIENT_COLOR,
  serverColor: null,
  isBannerVisible: false,
  isClientRefreshRequired: false,
  isLoggedIn: false,
  primedForVisibleAuthSession: false,

  syncOverlapStatus(status) {
    const nextClientRefreshRequired =
      get().isClientRefreshRequired ||
      shouldRequireClientRefresh(status, get().clientColor);
    const nextVisible = computeBannerVisible(
      status,
      get().clientColor,
      nextClientRefreshRequired,
    );
    const wasVisible = get().isBannerVisible;

    set({
      overlapStatus: status,
      isBannerVisible: nextVisible,
      isClientRefreshRequired: nextClientRefreshRequired,
    });

    if (!nextVisible && wasVisible) {
      set({ primedForVisibleAuthSession: false });
      return;
    }

    if (
      nextVisible &&
      !wasVisible &&
      get().isLoggedIn &&
      !get().primedForVisibleAuthSession
    ) {
      set({ primedForVisibleAuthSession: true });
      void new MatchmakingService().resolveRoute();
    }
  },

  syncServerColor(color) {
    const nextVisible = computeBannerVisible(
      get().overlapStatus,
      get().clientColor,
      get().isClientRefreshRequired,
    );
    const wasVisible = get().isBannerVisible;

    set({ serverColor: color, isBannerVisible: nextVisible });

    if (!nextVisible && wasVisible) {
      set({ primedForVisibleAuthSession: false });
      return;
    }

    if (
      nextVisible &&
      !wasVisible &&
      get().isLoggedIn &&
      !get().primedForVisibleAuthSession
    ) {
      set({ primedForVisibleAuthSession: true });
      void new MatchmakingService().resolveRoute();
    }
  },

  syncAuthState(isLoggedIn) {
    const wasLoggedIn = get().isLoggedIn;
    set({ isLoggedIn });

    if (!isLoggedIn) {
      set({ primedForVisibleAuthSession: false });
      return;
    }

    if (
      isLoggedIn &&
      !wasLoggedIn &&
      get().isBannerVisible &&
      !get().primedForVisibleAuthSession
    ) {
      set({ primedForVisibleAuthSession: true });
      void new MatchmakingService().resolveRoute();
    }
  },

  async refreshWithPrime() {
    if (!get().isBannerVisible) {
      return;
    }

    if (!get().isLoggedIn) {
      if (typeof window !== 'undefined') {
        window.location.reload();
      }

      return;
    }

    try {
      const response = await new MatchmakingService().resolveRoute();

      if (!response.success) {
        console.error(
          'Affinity priming failed before refresh',
          response.error ?? `HTTP ${response.status}`,
        );

        return;
      }
    } catch (error) {
      console.error('Affinity priming failed before refresh', error);
      return;
    }

    if (typeof window !== 'undefined') {
      window.location.reload();
    }
  },

  subscribeOverlapStatus() {
    let isActive = true;
    const eventSource = new HeartbeatEventSource(SSE_OVERLAP_STATUS_API_URL);
    const publicOverlapStatusService = new PublicOverlapStatusService();

    const fetchStatus = async (): Promise<void> => {
      if (!isActive) {
        return;
      }

      try {
        const response = await publicOverlapStatusService.getStatus();
        const status = response.data ?? null;

        if (status && isActive) {
          get().syncOverlapStatus(status);
        }
      } catch (error) {
        console.error('Failed to fetch overlap status', error);
      }
    };

    const fetchServerColor = async (): Promise<void> => {
      if (!isActive) {
        return;
      }

      try {
        const response = await fetch('/__server-color');

        if (!response.ok || !isActive) {
          return;
        }

        const data = (await response.json()) as { color: TAppColor | null };
        get().syncServerColor(data.color);
      } catch (error) {
        console.error('Failed to fetch server color', error);
      }
    };

    const statusListener = (message: MessageEvent<string>) => {
      const parsedStatus = jsonParseSafe<unknown>(message?.data);
      if (!isBillingOverlapPublicStatusDto(parsedStatus)) {
        return;
      }

      get().syncOverlapStatus(parsedStatus);
      void fetchServerColor();
    };

    eventSource.addEventListener(
      BILLING_OVERLAP_STATUS_SSE_EVENT,
      statusListener,
    );

    void fetchStatus();
    void fetchServerColor();

    const intervalId = setInterval(() => {
      void fetchServerColor();
    }, OVERLAP_POLL_INTERVAL_MS);

    return () => {
      isActive = false;
      eventSource.removeEventListener(
        BILLING_OVERLAP_STATUS_SSE_EVENT,
        statusListener,
      );
      eventSource.close();
      clearInterval(intervalId);
    };
  },
}));

export const useOverlapStore = <T>(selector: (state: TOverlapState) => T): T =>
  useStore(overlapStore, selector);

export const useOverlapBannerVisible = (): boolean =>
  useOverlapStore((s) => s.isBannerVisible);

export const useIsCurrentRouteDraining = (): boolean =>
  useOverlapStore((s) => s.isBannerVisible);
